import { z } from 'zod';

/** Most tables one guided labeling session can hold. */
export const MAX_LABELING_TABLES = 500;

export const labelingSessionSchema = z.object({
  ids: z.array(z.uuid()).min(1).max(MAX_LABELING_TABLES),
  completed: z.record(z.string(), z.enum(['accepted', 'skipped'])),
  includeSamples: z.boolean(),
});
export type LabelingSession = z.infer<typeof labelingSessionSchema>;

/**
 * Session for a launch that skips table selection. Resumes an unfinished saved
 * session over the same tables, so a refresh keeps accepted/skipped progress;
 * a finished one starts over so remaining drafts can be reviewed again.
 */
export function launchedLabelingSession(
  ids: readonly string[],
  saved: LabelingSession | null,
): LabelingSession {
  const savedIds = new Set(saved?.ids);
  const resumable =
    saved !== null &&
    savedIds.size === ids.length &&
    ids.every((id) => savedIds.has(id)) &&
    saved.ids.some((id) => !saved.completed[id]);
  return resumable
    ? saved
    : { ids: [...ids], completed: {}, includeSamples: false };
}

export function readLabelingSession(key: string): LabelingSession | null {
  try {
    const value = localStorage.getItem(key);
    if (!value) return null;
    const parsed = labelingSessionSchema.safeParse(JSON.parse(value));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function saveLabelingSession(
  key: string,
  value: LabelingSession,
): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}
